import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
interface CounterProps {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
}
function Counter({
  end,
  duration = 2000,
  suffix = '',
  prefix = ''
}: CounterProps) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number | null = null;
    const startValue = 0;
    const animate = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(startValue + (end - startValue) * easeOutQuart);
      setCount(current);
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);
  return <div ref={ref} className="text-5xl md:text-6xl font-medium mb-2" data-id="element-302">
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </div>;
}
const trustLines = ['Manufacturer-level support. In-house. Always.', 'Installations built to last 16 years.', 'Trusted by FM companies, landlords, and M&E contractors.'];
export function Stats() {
  return <section className="py-16 px-4 md:px-6" data-id="element-303">
      <div className="w-[95%] mx-auto bg-[#D4DDE8] rounded-3xl px-8 py-14 md:px-16 md:py-20 lg:px-20" data-id="element-304">
        <div className="grid md:grid-cols-2 gap-16 items-center mb-16" data-id="element-305">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-lg" data-id="element-306">
            <p className="text-xl md:text-2xl leading-relaxed text-primary-dark font-serif" data-id="element-307">
              AccuTemp is the UK's trusted commercial air conditioning
              specialist. Headquartered in Coventry with a nationwide presence,
              we deliver installation, maintenance, and compliance services
              built to last. Daikin D1+ Premier Partner status and over 16 years
              of experience.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-6" data-id="element-308">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="p-6" data-id="element-309">
              <Counter end={16} suffix="+" data-id="element-310" />
              <div className="text-sm text-primary-dark/60 uppercase tracking-wider" data-id="element-311">
                Years experience
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="p-6" data-id="element-312">
              <Counter end={1000} suffix="+" data-id="element-313" />
              <div className="text-sm text-primary-dark/60 uppercase tracking-wider" data-id="element-314">
                Commercial projects nationwide
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="p-6" data-id="element-315">
              <Counter end={100} prefix="£" data-id="element-316" />
              <div className="text-sm text-primary-dark/60 uppercase tracking-wider" data-id="element-317">
                Starting price + VAT
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="p-6" data-id="element-318">
              <div className="text-5xl md:text-6xl font-medium mb-2" data-id="element-319">
                1 in 100
              </div>
              <div className="text-sm text-primary-dark/60 uppercase tracking-wider" data-id="element-320">
                HVAC firms hold Constructionline Gold
              </div>
            </motion.div>
          </div>
        </div>

        {/* Trust lines */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-321">
          {trustLines.map((line, index) => <div key={index} className="text-sm text-primary-dark/70 leading-relaxed text-center md:text-left" data-id="element-322">
              {line}
            </div>)}
        </motion.div>
      </div>
    </section>;
}